import React, { useState } from "react";
import { Link } from "react-router-dom";
import { Logo } from "../brand/Logo";
import { Mascot } from "../brand/Mascot";

const NAV_ITEMS = [
  { label: "AI Builds", to: "/services/ai-app-development" },
  { label: "My Work", to: "/projects" },
  { label: "Coaching", to: "/pricing" },
  { label: "About", to: "/#founder" },
  { label: "Contact", to: "/#start-a-project" },
];

const LEGAL_ITEMS = [
  { label: "Manage Billing", to: "/billing" },
  { label: "Privacy", to: "/privacy" },
  { label: "Terms", to: "/terms" },
];

export const Footer = () => {
  const [waving, setWaving] = useState(false);
  const year = new Date().getFullYear();

  return (
    <footer
      className="relative"
      data-testid="footer-section"
      style={{
        borderTop: "1px solid var(--ggm-border-subtle)",
        background:
          "linear-gradient(180deg, transparent 0%, rgba(0,0,0,0.15) 100%)",
      }}
    >
      <div className="max-w-7xl mx-auto px-5 md:px-8 pt-14 pb-8 md:pt-16 md:pb-10 grid gap-10 md:gap-12 md:grid-cols-[1.5fr_1fr_1fr]">
        <div>
          {/* Larger, more brand-forward footer logo — tagline is baked into the artwork */}
          <Logo variant="horizontal" height={56} className="md:!h-[64px]" />
          <p className="mt-6 font-body text-white/60 text-sm leading-relaxed max-w-md">
            Turning ideas into AI agents, automations, applications, and working
            products.
          </p>
        </div>

        <nav aria-label="Footer navigation" data-testid="footer-nav">
          <div className="ggm-eyebrow mb-4">Navigate</div>
          <ul className="space-y-2.5 font-body text-sm text-white/70">
            {NAV_ITEMS.map((n) => (
              <li key={n.label}>
                <Link
                  to={n.to}
                  className="hover:text-[var(--ggm-signal-cyan)] transition-colors"
                  style={{ transitionDuration: "var(--ggm-duration-ui)" }}
                  data-testid={`footer-link-${n.label.toLowerCase().replace(/\s+/g, "-")}`}
                >
                  {n.label}
                </Link>
              </li>
            ))}
            <li>
              <Link
                to="/case-studies"
                className="hover:text-[var(--ggm-signal-cyan)] transition-colors"
                style={{ transitionDuration: "var(--ggm-duration-ui)" }}
                data-testid="footer-link-case-studies"
              >
                Case Studies
              </Link>
            </li>
            <li>
              <Link
                to="/notes"
                className="hover:text-[var(--ggm-signal-cyan)] transition-colors"
                style={{ transitionDuration: "var(--ggm-duration-ui)" }}
                data-testid="footer-link-notes"
              >
                Notes from the Shop
              </Link>
            </li>
          </ul>
        </nav>

        <div>
          <div className="ggm-eyebrow mb-4">Legal</div>
          <ul className="space-y-2.5 font-body text-sm text-white/70">
            {LEGAL_ITEMS.map((n) => (
              <li key={n.label}>
                <Link
                  to={n.to}
                  className="hover:text-[var(--ggm-signal-cyan)] transition-colors"
                  style={{ transitionDuration: "var(--ggm-duration-ui)" }}
                >
                  {n.label}
                </Link>
              </li>
            ))}
          </ul>
        </div>
      </div>

      <div
        className="border-t"
        style={{ borderColor: "var(--ggm-border-subtle)" }}
      >
        <div className="max-w-7xl mx-auto px-5 md:px-8 py-5 flex items-center justify-between gap-4">
          <p
            className="font-condensed uppercase tracking-widest text-[11px] text-white/45"
            data-testid="footer-copyright"
          >
            © {year} Gadget Guy Media. All rights reserved.
          </p>

          {/* Waving Gadget Guy Easter egg — modestly enlarged, still restrained */}
          <button
            type="button"
            className="relative shrink-0 rounded-full overflow-hidden focus:outline-none focus:ring-2 focus:ring-[var(--ggm-signal-cyan)]"
            style={{
              width: 56,
              height: 56,
              background: "rgba(230,237,247,0.06)",
              border: "1px solid var(--ggm-border-subtle)",
              transition: `transform var(--ggm-duration-ui) var(--ggm-ease-out)`,
              transform: waving ? "translateY(-2px) rotate(-6deg)" : "translateY(0) rotate(0deg)",
            }}
            onMouseEnter={() => setWaving(true)}
            onMouseLeave={() => setWaving(false)}
            onClick={() => {
              setWaving(true);
              setTimeout(() => setWaving(false), 900);
            }}
            aria-label="Gadget Guy waves hello"
            data-testid="footer-easter-egg"
          >
            <Mascot
              pose="waving"
              height={90}
              className="absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2"
            />
          </button>
        </div>
      </div>
    </footer>
  );
};

export default Footer;
